import { AnalyticsUser } from './interfaces/analyticsUser';
import { VideoType } from '../helpers/domain/videoTypes';
import { AnalyticsArticle } from './interfaces/analyticsArticle';
import { PostInfo } from '../../components/global/PostList/PostCard';
import { getArticleTypeInGA } from './helpers/articleTypeHelper';
import { AnalyticsProject } from './interfaces/analyticsProject';
import { sendAnalytics } from './analytics';
import {
  articleArrowEvent,
  articleAuthorAndTagEvent,
  articleAuthorAndTagFeedShowMoreEvent,
  articleAuthorAndTagFeedTabEvent,
  articleCarouselArrowEvent,
  articleEvent,
  articleFeedShowMoreEvent,
  articleFeedTabEvent,
  articleReadMoreEvent,
  articleShowMoreEvent,
  articleSrpEvent,
  articleSrpFeedShowMoreEvent,
  articleSrpFeedTabEvent,
  articleSubmittedCommentEvent,
  articleSubscribeButtonEvent,
  articleThumbnailEvent,
  articleTypeTabEvent,
  editorsPicksCarouselEvent,
  freeTextSearchEvent,
  GDPREvent,
  highLightEvent,
  inPageNavigationEvent,
  inPageSearchButtonEvent,
  loginEvent,
  navigationItemEvent,
  projectEvent,
  projectSubmittedCommentEvent,
  projectSubscribeButtonEvent,
  propertyTypeTabEvent,
  recommendedLocationCardEvent,
  recommendedLocationCarouselArrowEvent,
  registerEvent,
  relatedArticleEvent,
  relatedProjectEvent,
  selectFromSearchSuggestionEvent,
  viewMoreLinkEvent,
  ddListingEvent,
  ddListingCarouselArrowEvent,
} from './events/analyticsEvents';
import { HeroVideoInfo } from '../../queries/homePage/heroVideo';
import { MostPopular } from '../../queries/homePage/mostPopular';
import { LocationInfo } from '../../components/locationCard/LocationCard';
import { HighlightItem } from '../../queries/homePage/highlights';
import { HeroArticleInfo } from '../../queries/homePage/heroArticles';
import { convertToAnalyticsArticle } from './adapters/articleAdapter';
import { convertToAnalyticsProject } from './adapters/projectAdapter';

export const trackEditorsPicksArticle = (
  user: AnalyticsUser,
  editorsPikcs: PostInfo,
  analyticsSource: string,
  analyticsFeature: string,
) => {
  const analyticsArticle: AnalyticsArticle = convertToAnalyticsArticle(
    editorsPikcs,
    getArticleTypeInGA(
      editorsPikcs.category,
      editorsPikcs.videoType as VideoType,
    ),
  );
  const analyticsProject: AnalyticsProject = convertToAnalyticsProject(
    editorsPikcs.project,
  );
  sendAnalytics(
    articleEvent(
      user,
      analyticsArticle,
      analyticsProject,
      analyticsSource,
      analyticsFeature,
    ),
  );
};

export const trackMostPopularArticle = (
  user: AnalyticsUser,
  post: MostPopular,
  analyticsSource: string,
  analyticsFeature: string,
) => {
  const analyticsArticle: AnalyticsArticle = convertToAnalyticsArticle(
    post,
    getArticleTypeInGA(post.postCategory, post.videoType as VideoType),
  );
  const analyticsProject: AnalyticsProject = convertToAnalyticsProject(
    post.project,
  );
  sendAnalytics(
    articleEvent(
      user,
      analyticsArticle,
      analyticsProject,
      analyticsSource,
      analyticsFeature,
    ),
  );
};

export const trackRecommendedLocationCard = (
  user: AnalyticsUser,
  locationInfo: LocationInfo,
  analyticsSource: string,
  analyticsFeature: string,
) => {
  sendAnalytics(
    recommendedLocationCardEvent(
      user,
      locationInfo,
      analyticsSource,
      analyticsFeature,
    ),
  );
};

export const trackEditorsPicksCarousel = (
  user: AnalyticsUser,
  analyticsSource: string,
  analyticsFeature: string,
) => {
  sendAnalytics(
    editorsPicksCarouselEvent(user, analyticsSource, analyticsFeature),
  );
};

export const trackArticleFeedTab = (
  user: AnalyticsUser,
  analyticsSource: string,
  analyticsFeature: string,
  selectedTab: string,
) => {
  sendAnalytics(
    articleFeedTabEvent(user, analyticsSource, analyticsFeature, selectedTab),
  );
};

export const trackAuthorAndTagArticleFeedTab = (
  user: AnalyticsUser,
  analyticsSource: string,
  selectedTab: string,
) => {
  sendAnalytics(
    articleAuthorAndTagFeedTabEvent(user, analyticsSource, selectedTab),
  );
};

export const trackSrpArticleFeedTab = (
  user: AnalyticsUser,
  analyticsSource: string,
  selectedTab: string,
) => {
  sendAnalytics(articleSrpFeedTabEvent(user, analyticsSource, selectedTab));
};

export const trackArticleFeedShowMore = (
  user: AnalyticsUser,
  analyticsSource: string,
  analyticsFeature: string,
) => {
  sendAnalytics(
    articleFeedShowMoreEvent(user, analyticsSource, analyticsFeature),
  );
};

export const trackAuthorAndTagArticleFeedShowMore = (
  user: AnalyticsUser,
  analyticsSource: string,
) => {
  sendAnalytics(articleAuthorAndTagFeedShowMoreEvent(user, analyticsSource));
};

export const trackSrpArticleFeedShowMore = (
  user: AnalyticsUser,
  analyticsSource: string,
) => {
  sendAnalytics(articleSrpFeedShowMoreEvent(user, analyticsSource));
};

export const trackHeroVideoArticle = (
  user: AnalyticsUser,
  heroVideo: HeroVideoInfo,
  analyticsSource: string,
  analyticsFeature: string,
) => {
  const analyticsArticle: AnalyticsArticle = convertToAnalyticsArticle(
    heroVideo,
    getArticleTypeInGA(
      heroVideo.postCategory,
      heroVideo.videoType as VideoType,
    ),
  );
  const analyticsProject: AnalyticsProject = convertToAnalyticsProject(
    heroVideo.project,
  );
  sendAnalytics(
    articleEvent(
      user,
      analyticsArticle,
      analyticsProject,
      analyticsSource,
      analyticsFeature,
    ),
  );
};

export const trackRecommendedLocationCarouselArrow = (
  user: AnalyticsUser,
  analyticsSource: string,
  analyticsFeature: string,
) => {
  sendAnalytics(
    recommendedLocationCarouselArrowEvent(
      user,
      analyticsSource,
      analyticsFeature,
    ),
  );
};

export const trackHeroArticle = (
  user: AnalyticsUser,
  heroArticle: HeroArticleInfo,
  analyticsSource: string,
  analyticsFeature: string,
) => {
  const analyticsArticle: AnalyticsArticle = convertToAnalyticsArticle(
    heroArticle,
    getArticleTypeInGA(
      heroArticle.postCategory,
      heroArticle.videoType as VideoType,
    ),
  );
  const analyticsProject: AnalyticsProject = convertToAnalyticsProject(
    heroArticle.project,
  );
  sendAnalytics(
    articleEvent(
      user,
      analyticsArticle,
      analyticsProject,
      analyticsSource,
      analyticsFeature,
    ),
  );
};

export const trackHighLightArticle = (
  user: AnalyticsUser,
  highLight: HighlightItem,
  analyticsSource: string,
  analyticsFeature: string,
) => {
  sendAnalytics(
    highLightEvent(user, highLight.name, analyticsSource, analyticsFeature),
  );
};

export const trackPropertyTypeTab = (
  user: AnalyticsUser,
  analyticsSource: string,
  tabName: string,
) => {
  sendAnalytics(propertyTypeTabEvent(user, analyticsSource, tabName));
};

export const trackArticle = (
  user: AnalyticsUser,
  post: PostInfo,
  analyticsSource: string,
  analyticsFeature: string,
) => {
  const analyticsArticle: AnalyticsArticle = convertToAnalyticsArticle(
    post,
    getArticleTypeInGA(post.category, post.videoType as VideoType),
  );
  const analyticsProject: AnalyticsProject = convertToAnalyticsProject(
    post.project,
  );
  sendAnalytics(
    articleEvent(
      user,
      analyticsArticle,
      analyticsProject,
      analyticsSource,
      analyticsFeature,
    ),
  );
};

export const trackAuthorAndTagArticle = (
  user: AnalyticsUser,
  post: PostInfo,
  analyticsSource: string,
) => {
  const analyticsArticle: AnalyticsArticle = convertToAnalyticsArticle(
    post,
    getArticleTypeInGA(post.category, post.videoType as VideoType),
  );
  const analyticsProject: AnalyticsProject = convertToAnalyticsProject(
    post.project,
  );
  sendAnalytics(
    articleAuthorAndTagEvent(
      user,
      analyticsArticle,
      analyticsProject,
      analyticsSource,
    ),
  );
};

export const trackSrpArticle = (
  user: AnalyticsUser,
  post: PostInfo,
  analyticsSource: string,
) => {
  const analyticsArticle: AnalyticsArticle = convertToAnalyticsArticle(
    post,
    getArticleTypeInGA(post.category, post.videoType as VideoType),
  );
  const analyticsProject: AnalyticsProject = convertToAnalyticsProject(
    post.project,
  );
  sendAnalytics(
    articleSrpEvent(user, analyticsArticle, analyticsProject, analyticsSource),
  );
};

export const trackViewMoreLink = (
  user: AnalyticsUser,
  currentSource: string,
  targetSource: string,
  feature: string,
) => {
  sendAnalytics(viewMoreLinkEvent(user, currentSource, targetSource, feature));
};

export const trackInPageSearchButtonClick = (
  user: AnalyticsUser,
  source: string,
  locationType: string,
  locationName: string,
  selectedMaxValue: string,
  selectedMinValue: string,
  feature: string,
) => {
  sendAnalytics(
    inPageSearchButtonEvent(
      user,
      source,
      locationType,
      locationName,
      selectedMaxValue,
      selectedMinValue,
      feature,
    ),
  );
};

export const trackFreeTextSearch = (
  user: AnalyticsUser,
  source: string,
  keyword: string,
) => {
  sendAnalytics(freeTextSearchEvent(user, source, keyword));
};

export const trackArticleCarouselArrow = (
  user: AnalyticsUser,
  source: string,
) => {
  sendAnalytics(articleCarouselArrowEvent(user, source));
};

export const trackArticleShowMore = (user: AnalyticsUser, source: string) => {
  sendAnalytics(articleShowMoreEvent(user, source));
};

export const trackSelectFromSearchSuggestion = (
  user: AnalyticsUser,
  source: string,
  term: string,
  projectName: string,
  locationName: string,
  locationType: string,
) => {
  sendAnalytics(
    selectFromSearchSuggestionEvent(
      user,
      source,
      term,
      projectName,
      locationName,
      locationType,
    ),
  );
};

export const trackRecommendedVideo = (
  user: AnalyticsUser,
  article: AnalyticsArticle,
  project: AnalyticsProject,
  source: string,
  feature: string,
) => {
  sendAnalytics(articleEvent(user, article, project, source, feature));
};

export const trackProjectSubscribeButton = (
  user: AnalyticsUser,
  project: AnalyticsProject,
  source: string,
) => {
  sendAnalytics(projectSubscribeButtonEvent(user, project, source));
};

export const trackArticleReadMore = (
  user: AnalyticsUser,
  article: AnalyticsArticle,
  project: AnalyticsProject,
  source: string,
) => {
  sendAnalytics(articleReadMoreEvent(user, article, project, source));
};

export const trackArticleSubscribeButton = (
  user: AnalyticsUser,
  project: AnalyticsProject,
  article: AnalyticsArticle,
  source: string,
) => {
  sendAnalytics(articleSubscribeButtonEvent(user, article, project, source));
};

export const trackProjectClick = (
  user: AnalyticsUser,
  project: AnalyticsProject,
  source: string,
) => {
  sendAnalytics(projectEvent(user, project, source));
};

export const trackSubmittedArticleComment = (
  user: AnalyticsUser,
  article: AnalyticsArticle,
  project: AnalyticsProject,
  source: string,
) => {
  sendAnalytics(articleSubmittedCommentEvent(user, article, project, source));
};

export const trackSubmittedProjectComment = (
  user: AnalyticsUser,
  project: AnalyticsProject,
  source: string,
) => {
  sendAnalytics(projectSubmittedCommentEvent(user, project, source));
};

export const trackArticleArrow = (
  user: AnalyticsUser,
  article: AnalyticsArticle,
  source: string,
) => {
  sendAnalytics(articleArrowEvent(user, article, source));
};

export const trackShowGDPRMessagePopup = (
  user: AnalyticsUser,
  action: string,
  source: string,
) => {
  sendAnalytics(GDPREvent(user, action, source));
};

export const trackCloseGDPRMessage = (
  user: AnalyticsUser,
  action: string,
  source: string,
) => {
  sendAnalytics(GDPREvent(user, action, source));
};

export const trackLogin = (user: AnalyticsUser, source: string) => {
  sendAnalytics(loginEvent(user, source));
};

export const trackRegister = (user: AnalyticsUser, source: string) => {
  sendAnalytics(registerEvent(user, source));
};

export const trackArticleThumbnail = (
  user: AnalyticsUser,
  article: AnalyticsArticle,
  source: string,
  target: string,
) => {
  sendAnalytics(articleThumbnailEvent(user, article, source, target));
};

export const trackArticleTypeTab = (
  user: AnalyticsUser,
  source: string,
  target: string,
) => {
  sendAnalytics(articleTypeTabEvent(user, source, target));
};

export const trackRelatedArticle = (
  user: AnalyticsUser,
  article: AnalyticsArticle,
  project: AnalyticsProject,
  source: string,
) => {
  sendAnalytics(relatedArticleEvent(user, article, project, source));
};

export const trackRelatedProject = (
  user: AnalyticsUser,
  project: AnalyticsProject,
  source: string,
) => {
  sendAnalytics(relatedProjectEvent(user, project, source));
};

export const trackNavigationItemClick = (
  user: AnalyticsUser,
  source: string,
  targetSource: string,
) => {
  sendAnalytics(navigationItemEvent(user, source, targetSource));
};

export const trackInPageNavigation = (
  user: AnalyticsUser,
  article: AnalyticsArticle,
  source: string,
  target: string,
) => {
  sendAnalytics(inPageNavigationEvent(user, article, source, target));
};

export const trackDDListingClick = (
  user: AnalyticsUser,
  source: string,
  feature: string,
  listingIdentifier: string,
  eventData: string,
) => {
  sendAnalytics(
    ddListingEvent(user, source, feature, listingIdentifier, eventData),
  );
};

export const trackDDListingCarouselArrow = (
  user: AnalyticsUser,
  source: string,
  feature: string,
  listingIdentifier: string,
  direction: string,
) => {
  sendAnalytics(
    ddListingCarouselArrowEvent(
      user,
      source,
      feature,
      listingIdentifier,
      direction,
    ),
  );
};
